const GLOBAL_KEY = "__AVIV_CSNT__";
const USERCENTRICS_SCRIPT_IDENTIFIER = "usercentrics-cmp";
const USERCENTRICS_SCRIPT_SOURCE = "https://app.usercentrics.eu/browser-ui/latest/loader.js";
const USERCENTRICS_INITIALIZED_EVENT = "UC_UI_INITIALIZED";
const GIMII_SCRIPT_IDENTIFIER = "gimii-cmp";
const GIMII_SCRIPT_SOURCE = "https://static.gimii.fr/gimii.js";
const GOOGLE_PUBLISHER_TAG_SCRIPT_IDENTIFIER = "gpt-cmp";
const GOOGLE_PUBLISHER_TAG_SCRIPT_SOURCE = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
const UC_EVENT_MAPPER = {
    ACCEPT_ALL: "acceptButtonPressed",
    DENY_ALL: "denyButtonPressed",
    SAVE: "saveButtonPressed"
};

const createConsentService = async (/**
	 * The cookie consent provider settings identifier.
	 */ id, /**
	 * A set of options to manage callbacks and settings.
	 */ options)=>{
    // TODO: Add a developer warning to discourage direct usage of the UC global variable. This helps prevent leaky variable usage and supports a smoother long-term migration.
    // Instead, foster window.__AVIV_CSNT__ usage on non-React-based pages or `useConsentService` hook on React-based pages.
    let userCentricsService;
    try {
        userCentricsService = await getUserCentricsService(id, options);
        if (options?.gimiiRaiserId) {
            loadScript({
                id: GOOGLE_PUBLISHER_TAG_SCRIPT_IDENTIFIER,
                source: GOOGLE_PUBLISHER_TAG_SCRIPT_SOURCE
            });
            loadScript({
                id: GIMII_SCRIPT_IDENTIFIER,
                source: GIMII_SCRIPT_SOURCE,
                attributes: {
                    "data-raiser-id": options.gimiiRaiserId,
                    "data-ad-server": "google-ads"
                }
            });
        }
    } catch (error) {
        if (typeof options?.onError === "function") {
            options.onError(error);
        }
    }
    const service = {
        async getIsConsentGranted () {
            return Promise.resolve(!userCentricsService.isConsentRequired());
        },
        async getConsentItems () {
            return Promise.resolve(mapUserCentricsConsentItemsToConsentItems(userCentricsService.getServicesBaseInfo()));
        },
        async showBanner () {
            userCentricsService.showSecondLayer();
            return Promise.resolve();
        }
    };
    window.addEventListener("UC_UI_CMP_EVENT", (event)=>{
        const userCentricsEventType = event.detail.type;
        if ("DENY_ALL" === userCentricsEventType && options?.gimiiRaiserId) {
            window.Gimii?.forceExecute();
        }
        if (typeof options?.onBannerInteraction === "function" && [
            "ACCEPT_ALL",
            "DENY_ALL",
            "SAVE"
        ].includes(userCentricsEventType)) {
            const mappedEventType = UC_EVENT_MAPPER[userCentricsEventType];
            if (!mappedEventType) return;
            options.onBannerInteraction({
                event: mappedEventType,
                service
            });
        }
    });
    // Following eslint rule is disable to guarantee wider browser compatiblity:
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    window[GLOBAL_KEY] = window[GLOBAL_KEY] || {};
    for (const method of Object.keys(service)){
        // Add methods once available in the global namespace:
        window[GLOBAL_KEY][method] = service[method];
    }
    return service;
};
const mapUserCentricsConsentItemsToConsentItems = (userCentricsItems)=>{
    return userCentricsItems.reduce((items, item)=>{
        items[item.id] = {
            name: item.name,
            consent: item.consent.status,
            category: item.categorySlug
        };
        return items;
    }, {});
};
const getUserCentricsService = async (id, options)=>{
    return new Promise((resolve, reject)=>{
        if (window.UC_UI) {
            resolve(window.UC_UI);
            return;
        }
        if (!document.getElementById(USERCENTRICS_SCRIPT_IDENTIFIER)) {
            const usercentricsScript = document.createElement("script");
            usercentricsScript.id = USERCENTRICS_SCRIPT_IDENTIFIER;
            usercentricsScript.setAttribute("data-settings-id", id);
            // TODO: Set `data-disable-tracking` and `data-version="preview"` attribute for local/dev environment?
            usercentricsScript.src = USERCENTRICS_SCRIPT_SOURCE;
            if (options?.enableTCF ?? options?.enableTCF === undefined) {
                usercentricsScript.setAttribute("data-tcf-enabled", "");
            }
            document.head.appendChild(usercentricsScript);
            usercentricsScript.onerror = reject;
        }
        window.addEventListener(USERCENTRICS_INITIALIZED_EVENT, ()=>{
            if (window.UC_UI) {
                resolve(window.UC_UI);
                return;
            }
            reject(new Error("Usercentrics failed to load: its `UC_UI` global variable is not defined."));
        });
    });
};
const loadScript = ({ id, source, attributes })=>{
    if (document.getElementById(id) ?? document.head.querySelector(`script[src='${source}']`)) {
        return;
    }
    const scriptElement = document.createElement("script");
    scriptElement.id = id;
    scriptElement.src = source;
    if (attributes) {
        Object.entries(attributes).forEach(([name, value])=>{
            scriptElement.setAttribute(name, value);
        });
    }
    document.head.appendChild(scriptElement);
};

export { createConsentService };
